import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LanguageContext = createContext(null);
const RTL = new Set(["ar"]);
const LS = (l) => `si_i18n_${l}`;

// ---- Global DOM translation layer ----------------------------------------------------------------
// Rather than wrap every string in every component (impossible to keep complete), we translate the
// RENDERED DOM: walk visible text nodes + key attributes, translate each from its ENGLISH original
// via the existing i18n cache/flush pipeline, and swap it in. Because t() is a passthrough (React
// always emits English), the live English text is always the authoritative source — so there is no
// double-translation and switching between languages is race-free. Skips numbers/currency/emails,
// inputs/code, and anything under [data-no-translate].
const HAS_LETTER = /[A-Za-z\u00C0-\u024F]/;
const EMAIL_URL = /\S+@\S+|https?:\/\//i;
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA"]);
const ATTRS = ["placeholder", "title", "aria-label", "alt"];

function DomI18n() {
  const { lang, dict, version, register } = useLang();
  const origMap = useRef(new WeakMap());
  const lastOut = useRef(new WeakMap());
  const attrMap = useRef(new WeakMap());

  useEffect(() => {
    let timer = null, scheduled = false;
    const skip = (el) => {
      for (let n = el; n && n.tagName; n = n.parentElement) {
        if (SKIP_TAGS.has(n.tagName)) return true;
        if (n.hasAttribute && n.hasAttribute("data-no-translate")) return true;
        if (n.isContentEditable) return true;
      }
      return false;
    };
    const doText = (node) => {
      const parent = node.parentElement;
      if (!parent || skip(parent)) return;
      const cur = node.nodeValue;
      if (!cur || !cur.trim()) return;
      let orig;
      if (lastOut.current.get(node) === cur && origMap.current.has(node)) orig = origMap.current.get(node);
      else { orig = cur; origMap.current.set(node, cur); }
      const m = orig.match(/^(\s*)([\s\S]*?)(\s*)$/);
      const lead = m[1], core = m[2], trail = m[3];
      if (!core || !HAS_LETTER.test(core) || EMAIL_URL.test(core)) return;
      let coreOut;
      if (lang === "en") coreOut = core;
      else { const tr = dict[core]; if (tr === undefined) { register(core); coreOut = core; } else coreOut = tr; }
      const out = lead + coreOut + trail;
      if (node.nodeValue !== out) node.nodeValue = out;
      lastOut.current.set(node, out);
    };
    const doAttrs = (el) => {
      if (skip(el)) return;
      let store = attrMap.current.get(el);
      for (const a of ATTRS) {
        if (!el.hasAttribute(a)) continue;
        const cur = el.getAttribute(a);
        if (!cur) continue;
        const rec = store && store[a];
        const orig = rec && rec.last === cur ? rec.orig : cur;
        const core = orig.trim();
        if (!core || !HAS_LETTER.test(core) || EMAIL_URL.test(core)) continue;
        let out;
        if (lang === "en") out = orig;
        else { const tr = dict[core]; if (tr === undefined) { register(core); out = orig; } else out = orig.replace(core, tr); }
        if (el.getAttribute(a) !== out) el.setAttribute(a, out);
        if (!store) { store = {}; attrMap.current.set(el, store); }
        store[a] = { orig, last: out };
      }
    };
    const pass = () => {
      scheduled = false;
      const root = document.body;
      const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
      const nodes = [];
      while (w.nextNode()) nodes.push(w.currentNode);
      nodes.forEach(doText);
      root.querySelectorAll("[placeholder],[title],[aria-label],[alt]").forEach(doAttrs);
    };
    const schedule = () => { if (scheduled) return; scheduled = true; timer = setTimeout(pass, 40); };
    pass();
    const obs = new MutationObserver(schedule);
    obs.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => { obs.disconnect(); if (timer) clearTimeout(timer); };
  }, [lang, version, dict, register]);

  return null;
}

// Progressive i18n scaffold: English is the source of truth. The DOM layer above translates the
// rendered interface; t() is a passthrough kept for API compatibility with existing components.
export function LanguageProvider({ children }) {
  const { user } = useAuth();
  const [lang, setLangState] = useState(() => localStorage.getItem("si_lang") || "en");
  const [dict, setDict] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS(localStorage.getItem("si_lang") || "en")) || "{}"); }
    catch { return {}; }
  });
  const [languages, setLanguages] = useState([]);
  const [version, bump] = useState(0);
  const pending = useRef(new Set());
  const inflight = useRef(false);

  // Reconcile language to the ACTIVE user on every user/role switch. This runs on user.id
  // change (role switching re-issues a new user), so a previous user's language can never
  // carry over. Falls back to the business default, then English, when no preference is set.
  useEffect(() => {
    if (!user) return;
    const target = user.preferred_language || user.business_default_language || "en";
    if (target !== lang) applyLang(target, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.preferred_language]);

  useEffect(() => {
    document.documentElement.lang = lang;
    // Keep the layout left-to-right for every language (incl. Arabic). The UI is not RTL-safe,
    // so mirroring the whole page looked "flipped/broken"; text is still translated normally.
    document.documentElement.dir = "ltr";
  }, [lang]);

  useEffect(() => {
    // Languages load for BOTH anonymous (Hero) and authenticated users; anonymous uses the public endpoint.
    const url = user ? "/i18n/languages" : "/i18n/public/languages";
    api.get(url).then((r) => setLanguages(r.data.languages || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // PRELOAD: warm every supported language's cached dictionary into localStorage in the background,
  // right after load. Once warmed, switching language reads translations SYNCHRONOUSLY from
  // localStorage — no network wait, no LLM call, no English->translated flash — so it feels instant.
  useEffect(() => {
    if (!languages.length) return;
    let cancelled = false;
    const base = user ? "/i18n/bundle" : "/i18n/public/bundle";
    const codes = languages.map((l) => l.code).filter((c) => c !== "en");
    (async () => {
      for (const c of codes) {
        if (cancelled) return;
        try {
          const { data } = await api.get(`${base}?language=${c}`);
          const incoming = data.translations || {};
          let cur = {};
          try { cur = JSON.parse(localStorage.getItem(LS(c)) || "{}"); } catch { cur = {}; }
          const merged = { ...incoming, ...cur };
          localStorage.setItem(LS(c), JSON.stringify(merged));
          if (c === lang) { setDict((d) => ({ ...merged, ...d })); bump((n) => n + 1); }
        } catch { /* ignore */ }
        await new Promise((r) => setTimeout(r, 120)); // stagger to avoid a burst
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languages.length, user?.id]);

  const applyLang = (code, persistServer = true) => {
    setLangState(code);
    localStorage.setItem("si_lang", code);
    try { setDict(JSON.parse(localStorage.getItem(LS(code)) || "{}")); } catch { setDict({}); }
    document.documentElement.dir = "ltr";
    document.documentElement.lang = code;
    if (persistServer && user) api.put("/me/language", { language: code }).catch(() => {});
    // Instantly pull the full cached dictionary for this language in ONE request, so the whole
    // UI switches immediately. Anonymous visitors use the public (cache-only) bundle. English needs none.
    if (code !== "en") {
      const url = user ? `/i18n/bundle?language=${code}` : `/i18n/public/bundle?language=${code}`;
      api.get(url).then(({ data }) => {
        const incoming = data.translations || {};
        setDict((prev) => {
          const next = { ...incoming, ...prev }; // prev (localStorage) wins to avoid flicker
          try { localStorage.setItem(LS(code), JSON.stringify(next)); } catch { /* quota */ }
          return next;
        });
        bump((n) => n + 1);
      }).catch(() => {});
    }
  };

  const flush = useCallback(async () => {
    if (inflight.current || lang === "en" || pending.current.size === 0) return;
    const batch = Array.from(pending.current).slice(0, 120);
    pending.current = new Set(Array.from(pending.current).slice(120));
    inflight.current = true;
    try {
      // Authenticated users translate on demand; anonymous visitors use the cache-only public path.
      const url = user ? "/i18n/translate-batch" : "/i18n/public/translate-batch";
      const { data } = await api.post(url, { strings: batch, language: lang });
      setDict((prev) => {
        const next = { ...prev, ...data.translations };
        try { localStorage.setItem(LS(lang), JSON.stringify(next)); } catch { /* quota */ }
        return next;
      });
      bump((n) => n + 1);
    } catch { /* keep English */ }
    finally {
      inflight.current = false;
      if (pending.current.size > 0) flush(); // drain remaining immediately
    }
  }, [lang, user]);

  // Kick a flush the moment the language changes (don't wait for the interval) and poll frequently.
  useEffect(() => {
    const id = setTimeout(flush, 50);
    const t = setInterval(flush, 400);
    return () => { clearTimeout(id); clearInterval(t); };
  }, [flush]);

  const t = useCallback((text) => text, []); // passthrough — the DOM layer performs translation

  // Register a string discovered by the DOM layer for background translation (kick a prompt flush).
  const register = useCallback((text) => {
    if (!text || lang === "en" || dict[text] !== undefined) return;
    if (!pending.current.has(text)) { pending.current.add(text); setTimeout(flush, 60); }
  }, [lang, dict, flush]);

  return (
    <LanguageContext.Provider value={{ lang, setLang: applyLang, t, register, dict, version, languages, rtl: RTL.has(lang) }}>
      <DomI18n />
      {children}
    </LanguageContext.Provider>
  );
}

export const useLang = () => useContext(LanguageContext) || { lang: "en", t: (x) => x, setLang: () => {}, register: () => {}, dict: {}, version: 0, languages: [], rtl: false };
